import { useMemo, useRef, useState } from 'react'
import { MISSING_READINGS_MAX_DAYS, RangeToggle } from './controls'
import type { DeviceSeriesRow } from '../../../types/missingReadings'
import { deliveryBars, type DeliveryBar } from '../../../utils/dailyDelivery'
import { DeliveryLegend, deliveryBarTitle, shortDay } from '../../charts/DeliveryBars'

/**
 * Walk the missing-readings views one calendar day at a time.
 *
 * Every day in the window is reachable, not just the ones with misses. That is
 * deliberate: an operator comparing a bad day against the days either side of
 * it needs to see the clean ones, and a stepper that silently skipped them
 * would make a three-day outage look like three consecutive days.
 *
 * The bounds mirror parseHistoricalDay() in the API's constants/sigfox.ts —
 * today back to MISSING_READINGS_MAX_DAYS (365) days ago, inclusive, or the
 * lower fleet ceiling for a fleet view. Keeping them in step means the UI never
 * offers a day the API answers with INVALID_DATE.
 */

export const todayIso = () => new Date().toISOString().slice(0, 10)

export const shiftIso = (date: string, days: number): string => {
  const d = new Date(`${date}T00:00:00.000Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

export const earliestIso = (withinDays: number = MISSING_READINGS_MAX_DAYS) => shiftIso(todayIso(), -withinDays)

/**
 * The days currently in view: the zoom window if there is one, clamped to the
 * selected range, otherwise the whole range.
 *
 * Exported so the page's period stats use exactly the window the chart draws.
 * Computed separately, the two would drift the first time either clamping rule
 * changed, and the stats would quietly describe different days from the bars.
 */
export function visibleWindow(
  rangeDays: number,
  maxRangeDays: number,
  zoom: { from: string; to: string } | null | undefined
): { from: string; to: string } {
  const min = earliestIso(Math.min(rangeDays, maxRangeDays))
  const max = todayIso()
  if (!zoom) return { from: min, to: max }
  return { from: zoom.from < min ? min : zoom.from, to: zoom.to > max ? max : zoom.to }
}

/**
 * A day-of-month tick is written under every Nth bar. Every bar cannot carry
 * one — at 91 days across a page the cells are ~12px wide — so weekly ticks
 * give the eye something to count by without crowding.
 */
const TICK_EVERY = 7

/** Narrowest window keyboard zoom will go to — below a week the strip stops
 *  being a timeline and becomes a handful of fat bars. */
const MIN_ZOOM_DAYS = 7

/** Inclusive count of days from `from` to `to`. */
const daysInclusive = (from: string, to: string) =>
  Math.round((new Date(`${to}T00:00:00Z`).getTime() - new Date(`${from}T00:00:00Z`).getTime()) / 86_400_000) + 1

/**
 * The same bars as the device card and the dashboard strips, so a bar means
 * one thing everywhere: green and full when nothing was missed, red when
 * something was, shorter the less arrived, grey when there is nothing to judge.
 * Selection is an outline, never a colour, so it cannot be read as a result.
 */
const BAR_TONES: Record<DeliveryBar['tone'], string> = {
  ok: 'bg-emerald-400',
  missed: 'bg-red-500',
  unknown: 'bg-gray-300',
}

interface DayStepperProps {
  date: string
  onChange: (date: string) => void
  /** Day-by-day figures for the window, in any order; days with no row draw as no data. */
  rows: DeviceSeriesRow[]
  /** How far back the strip reaches. */
  rangeDays: number
  onRangeChange: (days: number) => void
  /** Largest window this view may ask for — lower for fleet-scoped callers. */
  maxRangeDays?: number
  /** Drag-selected sub-window of the range, or null for the whole range. */
  zoom?: { from: string; to: string } | null
  onZoomChange?: (next: { from: string; to: string } | null) => void
  /**
   * Days to pick out while something elsewhere on the page is hovered — every
   * other bar dims. Null when nothing is being brushed.
   */
  highlightDays?: Set<string> | null
}

export default function DayStepper({
  date,
  onChange,
  rows,
  rangeDays,
  onRangeChange,
  maxRangeDays = MISSING_READINGS_MAX_DAYS,
  zoom = null,
  onZoomChange,
  highlightDays = null,
}: DayStepperProps) {
  const max = todayIso()
  // The strip spans the selected range (see visibleWindow), but a day outside
  // it is still a valid destination — the date input stays bounded by the hard
  // ceiling so stepping past the edge of the strip is not an error.
  const inputMin = earliestIso(maxRangeDays)

  const prev = date > inputMin ? shiftIso(date, -1) : null
  const next = date < max ? shiftIso(date, 1) : null

  // The whole window, every time — the strip does not follow the selection.
  // A strip that slid around the selected day would change what it shows as
  // you step through it, which makes comparing one day against the shape of
  // the month impossible.
  // Zoom is a pure display concern: it narrows what the strip draws, never
  // what the page fetched, so zooming never costs a request.
  const { from: stripFrom, to: stripTo } = visibleWindow(rangeDays, maxRangeDays, zoom)

  const strip = useMemo(() => {
    const days: string[] = []
    for (let day = stripFrom; day <= stripTo; day = shiftIso(day, 1)) days.push(day)
    return days
  }, [stripFrom, stripTo])

  const barsByDay = useMemo(() => new Map(deliveryBars(rows, new Date()).map((bar) => [bar.day, bar])), [rows])

  const step = (to: string | null) => {
    if (to) onChange(to)
  }

  // A real tooltip rather than the native `title`: at 90+ bars the browser's
  // ~1s delay makes scrubbing the chart useless, and it renders wherever the
  // cursor is rather than over the bar it describes.
  // Drag-to-zoom. `dragged` guards the click handler: without it, releasing a
  // drag also selects whichever day the mouse came up on.
  const [dragStart, setDragStart] = useState<number | null>(null)
  const [dragEnd, setDragEnd] = useState<number | null>(null)
  const dragged = useRef(false)

  const dragLo = dragStart === null || dragEnd === null ? null : Math.min(dragStart, dragEnd)
  const dragHi = dragStart === null || dragEnd === null ? null : Math.max(dragStart, dragEnd)

  const commitDrag = () => {
    if (dragLo !== null && dragHi !== null && dragHi > dragLo && onZoomChange) {
      onZoomChange({ from: strip[dragLo], to: strip[dragHi] })
      dragged.current = true
    }
    setDragStart(null)
    setDragEnd(null)
  }

  const [hovered, setHovered] = useState<number | null>(null)
  const hoveredDay = hovered === null ? null : strip[hovered]

  // Keyboard zoom — the drag is mouse-only, so without these there is no way
  // in or out from a keyboard at all.
  const fullRange = visibleWindow(rangeDays, maxRangeDays, null)
  const fullSpan = daysInclusive(fullRange.from, fullRange.to)

  /** A window of `span` days centred on `centre`, slid back inside the range. */
  const windowAround = (centre: string, span: number) => {
    let from = shiftIso(centre, -Math.floor((span - 1) / 2))
    let to = shiftIso(from, span - 1)
    if (from < fullRange.from) {
      from = fullRange.from
      to = shiftIso(from, span - 1)
    }
    if (to > fullRange.to) {
      to = fullRange.to
      from = shiftIso(to, -(span - 1))
    }
    return { from, to }
  }

  const zoomBy = (factor: number) => {
    if (!onZoomChange) return
    const span = strip.length
    const nextSpan = factor < 1 ? Math.max(MIN_ZOOM_DAYS, Math.floor(span * factor)) : Math.ceil(span * factor)
    if (nextSpan >= fullSpan) {
      onZoomChange(null)
      return
    }
    if (nextSpan === span) return
    // Zoom around the selected day when it is on screen — that is the day the
    // reader is looking at — otherwise around the middle of what is shown.
    const centre = date >= stripFrom && date <= stripTo ? date : strip[Math.floor(span / 2)]
    onZoomChange(windowAround(centre, nextSpan))
  }

  return (
    <div
      className="space-y-2"
      // Arrow keys are how anyone actually scrubs a date range; the buttons are
      // here for discoverability, not as the only way in.
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault()
          step(prev)
        } else if (e.key === 'ArrowRight') {
          e.preventDefault()
          step(next)
        } else if ((e.target as HTMLElement).tagName === 'INPUT') {
          // Leave the date field's own keys alone — Esc closes its picker.
          return
        } else if (e.key === 'Escape') {
          // A drag in progress is cancelled first; only a second Esc clears
          // the zoom, so abandoning a drag never throws away the current view.
          if (dragStart !== null) {
            setDragStart(null)
            setDragEnd(null)
          } else if (zoom) {
            onZoomChange?.(null)
          }
        } else if (e.key === '+' || e.key === '=') {
          e.preventDefault()
          zoomBy(0.5)
        } else if (e.key === '-' || e.key === '_') {
          e.preventDefault()
          zoomBy(2)
        }
      }}
    >
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => step(prev)}
          disabled={!prev}
          aria-label="Previous day"
          title="Previous day"
          className="px-2 py-1 rounded text-base leading-none text-blue-600 hover:bg-blue-50 disabled:text-gray-300 disabled:hover:bg-transparent"
        >
          ‹
        </button>
        <input
          type="date"
          aria-label="Day"
          value={date}
          min={inputMin}
          max={max}
          onChange={(e) => e.target.value && onChange(e.target.value)}
          className="border border-gray-200 rounded px-2 py-1 text-sm text-gray-700"
        />
        <button
          type="button"
          onClick={() => step(next)}
          disabled={!next}
          aria-label="Next day"
          title="Next day"
          className="px-2 py-1 rounded text-base leading-none text-blue-600 hover:bg-blue-50 disabled:text-gray-300 disabled:hover:bg-transparent"
        >
          ›
        </button>

        {/* Range selector. The strip redraws to the chosen window, so this is
            also how far back the page's own data query reaches. Picking a range
            also clears any zoom: "show me this whole window" contradicts a
            stale zoom, so these buttons double as the way back out. */}
        <div className="ml-2">
          <RangeToggle
            value={rangeDays}
            maxDays={maxRangeDays}
            onChange={(d) => {
              onZoomChange?.(null)
              onRangeChange(d)
            }}
          />
        </div>
      </div>

      {zoom && (
        <div className="flex items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1.5 rounded bg-blue-50 px-2 py-0.5 text-blue-800">
            <span className="tabular-nums">
              {stripFrom} → {stripTo}
            </span>
            <span className="text-blue-400">({strip.length} days)</span>
          </span>
          <button
            type="button"
            onClick={() => onZoomChange?.(null)}
            className="text-gray-500 hover:text-gray-700 hover:underline"
          >
            Reset zoom
          </button>
        </div>
      )}

      {/* The whole window, not a slice of it: the point of the strip is to
          show where the bad days sit across the whole selected window, so it
          stretches to fill the page rather than sitting beside the controls. A clean day still draws a
          stub bar so the timeline stays continuous. */}
      <div className="w-full relative">
        {hoveredDay && (
          <div
            role="tooltip"
            className="pointer-events-none absolute -top-1 z-10 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-[11px] text-white shadow"
            style={{ left: `${((hovered! + 0.5) / strip.length) * 100}%` }}
          >
            <span className="tabular-nums">{tooltipText(hoveredDay, barsByDay.get(hoveredDay))}</span>
          </div>
        )}
        <div
          className="flex items-end gap-px h-12 w-full select-none"
          onMouseLeave={() => {
            setHovered(null)
            commitDrag()
          }}
          onMouseUp={commitDrag}
        >
          {strip.map((day, index) => {
            const bar = barsByDay.get(day)
            const selected = day === date
            return (
              <button
                key={day}
                type="button"
                onClick={() => {
                  // Swallow the click that ends a drag.
                  if (dragged.current) {
                    dragged.current = false
                    return
                  }
                  onChange(day)
                }}
                onMouseDown={() => {
                  setDragStart(index)
                  setDragEnd(index)
                }}
                onMouseEnter={() => {
                  setHovered(index)
                  if (dragStart !== null) setDragEnd(index)
                }}
                onFocus={() => setHovered(index)}
                onBlur={() => setHovered(null)}
                aria-label={tooltipText(day, bar)}
                aria-current={selected ? 'date' : undefined}
                className={`flex-1 min-w-0 h-full flex items-end rounded-sm ${
                  dragLo !== null && dragHi !== null && index >= dragLo && index <= dragHi
                    ? 'bg-blue-200'
                    : selected
                    ? 'bg-blue-50'
                    : 'hover:bg-gray-100'
                }`}
              >
                <span
                  className={`block w-full rounded-sm transition-opacity ${bar ? BAR_TONES[bar.tone] : 'bg-gray-200'} ${
                    selected ? 'ring-2 ring-blue-600 ring-offset-1' : ''
                  } ${bar?.partial ? 'opacity-70' : ''} ${highlightDays && !highlightDays.has(day) ? 'opacity-20' : ''}`}
                  style={{ height: bar ? `${Math.max(bar.fill * 100, 8)}%` : '8%' }}
                />
              </button>
            )
          })}
        </div>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-3 text-[11px] text-gray-500">
          {onZoomChange && (
            // Buttons as well as the drag and the keys: a drag is mouse-only
            // and the keys are invisible, so without these a tablet user has
            // no way in and nobody discovers the feature.
            <span className="flex flex-wrap items-center gap-2">
              <span className="inline-flex overflow-hidden rounded border border-gray-200">
                <button
                  type="button"
                  onClick={() => zoomBy(0.5)}
                  disabled={strip.length <= MIN_ZOOM_DAYS}
                  className="px-2 py-0.5 text-gray-700 hover:bg-gray-50 disabled:text-gray-300 disabled:hover:bg-transparent"
                >
                  + Zoom in
                </button>
                <button
                  type="button"
                  onClick={() => zoomBy(2)}
                  disabled={!zoom}
                  className="border-l border-gray-200 px-2 py-0.5 text-gray-700 hover:bg-gray-50 disabled:text-gray-300 disabled:hover:bg-transparent"
                >
                  − Zoom out
                </button>
              </span>
              <span>
                or drag across the bars
                {zoom ? (
                  <>
                    {' '}
                    · <kbd className="font-sans">Esc</kbd> to reset
                  </>
                ) : null}
              </span>
            </span>
          )}
          <DeliveryLegend />
        </div>
        <div className="flex gap-px w-full mt-1" aria-hidden="true">
          {strip.map((day, index) => (
            <span
              key={day}
              className={`flex-1 min-w-0 text-center text-[10px] tabular-nums ${
                day === date ? 'text-blue-700 font-semibold' : 'text-gray-500'
              }`}
            >
              {day === date || index % TICK_EVERY === 0 ? day.slice(8) : ''}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

/** What a bar says, in words: the tooltip, and the bar's accessible name. */
function tooltipText(day: string, bar: DeliveryBar | undefined): string {
  const label = shortDay(day)
  if (!bar) return `${label} · no messages received`
  if (bar.row.missedMessages === 0) return `${label} · no missed messages`
  return deliveryBarTitle(bar).replace(`${day}:`, `${label} ·`)
}
